import { useUserMe } from "~/components/user/composables/useUser";
import { useActorFields } from "~/composables/useActorFields";
import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import type { Database } from "@asap/shared/types/supabase.gen";
import { ppeRequestColumns } from "@asap/shared/utils/supabase.columns";
import {
  getPpeRequestSortOrder,
  type PpePickupLocation,
  type PpeRequestListFilters,
} from "~/components/ppe-request/ppe-request.model";

type PpeRequestUpdate = Database["public"]["Tables"]["ppe_request"]["Update"];
type PpeRequestInsert = Database["public"]["Tables"]["ppe_request"]["Insert"];
type PpeRequestItemUpdate = Database["public"]["Tables"]["ppe_request_item"]["Update"];

export type PpeRequestCreateValues = PpeRequestInsert & { ppes: { item_id: string; attribute: string }[] };

// Supabase calls are legacy, kept as-is until the matching apps/api endpoints exist.
export const usePpeRequestApi = () => {
  const supabase = useSupabase();
  const { invoke } = useApi();
  const { actorFields } = useActorFields();
  const { workspaceId, isCSM } = useUserMe();

  const listForTable = async (filters: PpeRequestListFilters) => {
    if (!workspaceId.value) throw new Error("Workspace ID is not defined");
    const order = getPpeRequestSortOrder(filters.sorting);

    const response = await invoke({
      path: "ppe-request",
      method: "GET",
      query: {
        status: filters.status === "all" ? undefined : [filters.status],
        date: filters.selectedDate?.toISOString(),
        businessUnitIds: filters.businessUnitIds,
        wsmTeamMemberIds: filters.wsmTeamMemberIds,
        consultantTeamMemberIds: filters.consultantTeamMemberIds,
        workspaceId: isCSM.value ? undefined : workspaceId.value,
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        orderColumn: order?.column,
        orderDirection: order?.direction,
      },
    });
    if (!response.success) throw new Error(response.error.message);

    return { items: response.data.items, totalItems: response.data.totalItems };
  };

  const getById = async (id: string) => {
    const { data, error } = await supabase.from("ppe_request").select(ppeRequestColumns).eq("id", id).single();
    if (error) throw new Error(error.message);
    return data;
  };

  const update = async (id: string, values: PpeRequestUpdate) => {
    const { updated_by, updated_at } = actorFields();
    const { error } = await supabase
      .from("ppe_request")
      .update({ ...values, updated_by, updated_at })
      .eq("id", id);
    if (error) throw new Error(error.message);
  };

  const create = async ({ ppes, ...request }: PpeRequestCreateValues) => {
    const { created_by } = actorFields();
    const { data, error } = await supabase
      .from("ppe_request")
      .insert({ ...request, created_by })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    const { error: insertItemsError } = await supabase
      .from("ppe_request_item")
      .insert(ppes.map((ppe) => ({ request_id: data.id, item_id: ppe.item_id, attribute: ppe.attribute })));
    if (insertItemsError) {
      await supabase.from("ppe_request").delete().eq("id", data.id);
      throw new Error("Failed to insert ppes linked to request");
    }

    return data;
  };

  const updateItem = async (id: string, values: PpeRequestItemUpdate) => {
    const { error } = await supabase.from("ppe_request_item").update(values).eq("id", id);
    if (error) throw new Error(error.message);
  };

  const approve = async (id: string, approval: { price: number; wsmId: string | null }) => {
    const response = await invoke({
      path: "ppe-request/:id/approval",
      method: "PUT",
      params: { id },
      body: { price: approval.price, wsm_id: approval.wsmId },
    });
    if (!response.success) throw new Error(response.error.message);
  };

  const sendPickupEmails = async (email: {
    talentId: string;
    ppeRequestId: string;
    pickupLocation: PpePickupLocation;
    wsmComment: string;
  }) => {
    const response = await invoke({
      path: "ppe-request/send-emails",
      method: "POST",
      body: {
        talent_id: email.talentId,
        ppe_request_id: email.ppeRequestId,
        pickup_location: email.pickupLocation,
        wsm_comment: email.wsmComment,
      },
    });
    if (!response.success) throw new Error(response.error.message);
  };

  return { listForTable, getById, update, create, updateItem, approve, sendPickupEmails };
};
